/**
 * Step identity and ordering for the onboarding wizard.
 *
 * Kept in its own module, importing nothing, because both the store — which
 * maintains the furthest-reached high-water mark and therefore has to compare
 * two steps — and `../config/constants` need the order. Declaring the enum
 * beside the store and the order beside the config would put those two modules
 * in a cycle, and the arrays below evaluate at module load, so the cycle would
 * read the enum before it is initialised.
 */
export enum OnboardingSteps {
  Welcome = 'welcome',
  // New Company
  Structure = 'structure',
  CompanyInformation = 'company-information',
  Shareholders = 'shareholders',
  Roles = 'roles',
  Summary = 'summary',
  Packages = 'packages',
  // Existing Company
  ExistingCompanyName = 'existing-company-name',
  ExistingShareholders = 'existing-shareholders',
  ExistingDocuments = 'existing-documents',
  ExistingCompanyInformation = 'existing-company-information',
}

export const STEP_ORDER: OnboardingSteps[] = [
  OnboardingSteps.Welcome,
  OnboardingSteps.Structure,
  OnboardingSteps.CompanyInformation,
  OnboardingSteps.Shareholders,
  OnboardingSteps.Roles,
  OnboardingSteps.Summary,
  OnboardingSteps.Packages,
] as const

export const EXISTING_COMPANY_STEP_ORDER: OnboardingSteps[] = [
  OnboardingSteps.Welcome,
  OnboardingSteps.ExistingCompanyName,
  OnboardingSteps.ExistingShareholders,
  OnboardingSteps.ExistingDocuments,
  OnboardingSteps.ExistingCompanyInformation,
] as const

export function stepOrderFor(onboardingType: 'new' | 'existing'): OnboardingSteps[] {
  return onboardingType === 'existing' ? EXISTING_COMPANY_STEP_ORDER : STEP_ORDER
}

/** Position of `step` in its flow, or -1 when the step belongs to the other flow. */
export function stepIndexFor(step: OnboardingSteps, onboardingType: 'new' | 'existing'): number {
  return stepOrderFor(onboardingType).indexOf(step)
}

/** Is this one of the wizard's steps at all? */
function isOnboardingStep(value: unknown): value is OnboardingSteps {
  return typeof value === 'string' && STEP_ORDER.concat(EXISTING_COMPANY_STEP_ORDER).includes(value as OnboardingSteps)
}

/**
 * A flow type and step that are guaranteed to agree.
 *
 * When they do not, everything downstream reads `indexOf(step) === -1` as
 * "before the beginning": no sidebar entry is offered, not even Welcome, and
 * the step switch falls through to its `default`, leaving a blank page with no
 * navigation on it. So the pair is reconciled once, here, before it is written
 * anywhere.
 *
 * - A missing or unrecognised flow type is INFERRED from the step. This is the
 *   case seen in the wild — a bootstrap payload with no `onboardingType` beside
 *   an existing-company step — and inferring keeps the customer in the flow
 *   they were actually working in.
 * - An explicit flow type beside a step from the other flow is contradictory
 *   rather than incomplete; the stated type is kept and the step falls back to
 *   Welcome, which belongs to both orders and is therefore always reachable.
 */
export function resolveOnboardingFlow(
  rawOnboardingType: unknown,
  rawStep: unknown,
): { onboardingType: 'new' | 'existing'; step: OnboardingSteps } {
  const step = isOnboardingStep(rawStep) ? rawStep : null

  const onboardingType: 'new' | 'existing' =
    rawOnboardingType === 'new' || rawOnboardingType === 'existing'
      ? rawOnboardingType
      : step && EXISTING_COMPANY_STEP_ORDER.includes(step) && !STEP_ORDER.includes(step)
        ? 'existing'
        : 'new'

  return {
    onboardingType,
    step: step && stepIndexFor(step, onboardingType) >= 0 ? step : OnboardingSteps.Welcome,
  }
}
